import React from 'react'
import { User } from 'lucide-react'
import { getInitials } from '@/utils/formatting'

export type CallRingState =
  | 'ringing'
  | 'active'
  | 'hold'
  | 'poor'
  | 'failed'
  | 'post-call'
  | 'neutral'

type Size = 'xs' | 'sm' | 'md' | 'lg'

interface AvatarStateRingProps {
  avatarUrl?: string | null
  initials?: string | null
  name?: string | null
  state: CallRingState
  size?: Size
  pulse?: boolean
  className?: string
}

interface RingTone {
  ring: string
  ringSoft: string
  tint: string
  border: string
}

// Phase rule: an incoming call needs you now (white), a connected call is
// "on" (mint), a bad line or a failed call is an error (blush), and hold is
// just waiting (neutral glass).
const TONES: Record<CallRingState, RingTone> = {
  ringing: {
    ring: 'rgba(255,255,255,0.70)',
    ringSoft: 'rgba(255,255,255,0.22)',
    tint: 'rgba(255,255,255,0.08)',
    border: 'rgba(255,255,255,0.30)',
  },
  active: {
    ring: 'rgba(168,230,206,0.55)',
    ringSoft: 'rgba(168,230,206,0.22)',
    tint: 'rgba(168,230,206,0.10)',
    border: 'rgba(168,230,206,0.30)',
  },
  hold: {
    ring: 'rgba(255,255,255,0.28)',
    ringSoft: 'rgba(255,255,255,0.10)',
    tint: 'rgba(255,255,255,0.06)',
    border: 'rgba(255,255,255,0.12)',
  },
  poor: {
    ring: 'rgba(244,181,189,0.55)',
    ringSoft: 'rgba(244,181,189,0.22)',
    tint: 'rgba(244,181,189,0.10)',
    border: 'rgba(244,181,189,0.30)',
  },
  failed: {
    ring: 'rgba(244,181,189,0.70)',
    ringSoft: 'rgba(244,181,189,0.28)',
    tint: 'rgba(244,181,189,0.14)',
    border: 'rgba(244,181,189,0.40)',
  },
  'post-call': {
    ring: 'rgba(255,255,255,0.14)',
    ringSoft: 'rgba(255,255,255,0.06)',
    tint: 'rgba(255,255,255,0.04)',
    border: 'rgba(255,255,255,0.08)',
  },
  neutral: {
    ring: 'rgba(255,255,255,0.18)',
    ringSoft: 'rgba(255,255,255,0.08)',
    tint: 'rgba(255,255,255,0.04)',
    border: 'rgba(255,255,255,0.08)',
  },
}

const SIZES: Record<Size, { avatar: number; ring: number; icon: number; font: number }> = {
  xs: { avatar: 36,  ring: 4, icon: 14, font: 12 },
  sm: { avatar: 64,  ring: 6, icon: 24, font: 20 },
  md: { avatar: 96,  ring: 8, icon: 36, font: 28 },
  lg: { avatar: 128, ring: 10, icon: 48, font: 36 },
}

/**
 * AvatarStateRing — circular avatar wrapped in a state-tinted ring.
 * The ring color encodes call state (ringing/active/hold/poor/failed/post-call/neutral)
 * and transitions smoothly on prop change. When `pulse` is true and the user has not
 * requested reduced motion, the ring also breathes.
 *
 * MIDNIGHT v3.0 compliant — phase-rule ring tints, no hardcoded reds, ring color
 * transitions in 400ms via the canonical MIDNIGHT easing curve.
 */
export function AvatarStateRing({
  avatarUrl,
  initials,
  name,
  state,
  size = 'md',
  pulse = false,
  className,
}: AvatarStateRingProps) {
  const dims = SIZES[size]
  const tone = TONES[state]
  const displayInitials = (initials ?? (name ? getInitials(name) : '')).slice(0, 2).toUpperCase()
  const animate = pulse ? 'avatar-ring-pulse' : ''

  return (
    <div
      className={[className, 'relative inline-flex items-center justify-center', animate]
        .filter(Boolean)
        .join(' ')}
      style={{
        width: dims.avatar + dims.ring * 4,
        height: dims.avatar + dims.ring * 4,
        // Expose ring tones as CSS vars so the ::before pulse keyframes can pick them up.
        ['--ring-tone' as string]: tone.ring,
        ['--ring-tone-soft' as string]: tone.ringSoft,
      }}
      aria-hidden={!name}
    >
      {/* Outer state ring */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '9999px',
          border: `${dims.ring / 2}px solid ${tone.ring}`,
          boxShadow: `0 0 24px -4px ${tone.ringSoft}`,
          transition:
            'border-color 0.4s cubic-bezier(0.2,0.8,0.2,1), box-shadow 0.4s cubic-bezier(0.2,0.8,0.2,1)',
        }}
      />
      {/* Avatar */}
      <span
        style={{
          position: 'relative',
          zIndex: 1,
          width: dims.avatar,
          height: dims.avatar,
          borderRadius: '9999px',
          background: avatarUrl ? '#0A0A0C' : tone.tint,
          border: `0.5px solid ${tone.border}`,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(255,255,255,0.85)',
          fontWeight: 600,
          fontSize: dims.font,
          letterSpacing: '-0.02em',
          fontFeatureSettings: "'ss01','cv01','cv02'",
          transition: 'background 0.4s cubic-bezier(0.2,0.8,0.2,1)',
        }}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={name ?? ''}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : displayInitials ? (
          displayInitials
        ) : (
          <User
            aria-hidden="true"
            style={{ width: dims.icon, height: dims.icon, color: 'rgba(255,255,255,0.55)' }}
          />
        )}
      </span>
    </div>
  )
}
